import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  ShoppingCart,
  Plus,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Store,
} from 'lucide-react';

const emptySolicitacao = () => ({
  tipo_equipamento_id: '',
  descricao_necessidade: '',
  apelido_sugerido: '',
  dias_para_resposta: 14,
});

const emptyAceite = () => ({
  apelido: '',
  setor: '',
  tag_patrimonio: '',
  vida_util_anos: 10,
});

const CotacoesEquipamentosPage = () => {
  const { getEmpresaId, hasPermission } = useAuth();
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNova, setShowNova] = useState(false);
  const [form, setForm] = useState(emptySolicitacao());
  const [sel, setSel] = useState(null);
  const [cotacoes, setCotacoes] = useState([]);
  const [loadingCot, setLoadingCot] = useState(false);
  const [aceiteCotacao, setAceiteCotacao] = useState(null);
  const [aceiteForm, setAceiteForm] = useState(emptyAceite());
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const empresa_id = getEmpresaId();
      const [rSol, rTipos, rCat] = await Promise.all([
        httpsCallable(functions, 'listSolicitacoesCotacaoEquipamento')({ empresa_id }),
        httpsCallable(functions, 'listTiposEquipamento')({ apenas_ativos: true }),
        httpsCallable(functions, 'listCatalogoMarketplaceEquipamentos')({}),
      ]);
      setSolicitacoes(rSol.data?.data?.items || []);
      setTipos(rTipos.data?.data?.items || []);
      setCatalogo(rCat.data?.data?.items || []);
    } catch (e) {
      setErr(e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [getEmpresaId]);

  const abrirCotacoes = async (sol) => {
    setSel(sol);
    setLoadingCot(true);
    try {
      const r = await httpsCallable(functions, 'listCotacoesEquipamentoDaSolicitacao')({
        solicitacao_id: sol.id,
        empresa_id: getEmpresaId(),
      });
      setCotacoes(r.data?.data?.items || []);
    } catch (e) {
      alert(e.message || 'Erro ao carregar cotações');
    } finally {
      setLoadingCot(false);
    }
  };

  const criarSolicitacao = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await httpsCallable(functions, 'criarSolicitacaoCotacaoEquipamento')({
        empresa_id: getEmpresaId(),
        ...form,
      });
      setShowNova(false);
      setForm(emptySolicitacao());
      setMsg('Solicitação enviada aos fornecedores.');
      await load();
    } catch (ex) {
      setErr(ex.message || 'Erro ao criar solicitação');
    } finally {
      setLoading(false);
    }
  };

  const cancelar = async (id) => {
    if (!window.confirm('Cancelar esta solicitação?')) return;
    try {
      await httpsCallable(functions, 'cancelarSolicitacaoCotacaoEquipamento')({
        empresa_id: getEmpresaId(),
        solicitacao_id: id,
      });
      await load();
      setSel(null);
    } catch (ex) {
      alert(ex.message || 'Erro ao cancelar');
    }
  };

  const confirmarAceite = async (e) => {
    e.preventDefault();
    if (!aceiteCotacao) return;
    setLoading(true);
    try {
      const r = await httpsCallable(functions, 'aceitarCotacaoEquipamento')({
        empresa_id: getEmpresaId(),
        cotacao_id: aceiteCotacao.id,
        ...aceiteForm,
      });
      alert(`Equipamento criado: ${r.data?.data?.equipamento_id}`);
      setAceiteCotacao(null);
      setSel(null);
      await load();
    } catch (ex) {
      alert(ex.message || 'Erro ao aceitar cotação');
    } finally {
      setLoading(false);
    }
  };

  const statusLabel = (s) => {
    const map = {
      pendente: { label: 'Pendente', cls: 'bg-yellow-100 text-yellow-800' },
      cotacoes_recebidas: { label: 'Com cotações', cls: 'bg-blue-100 text-blue-800' },
      equipamento_criado: { label: 'Comprado', cls: 'bg-green-100 text-green-800' },
      cancelada: { label: 'Cancelada', cls: 'bg-gray-100 text-gray-600' },
    };
    return map[s] || { label: s, cls: 'bg-gray-100' };
  };

  if (!hasPermission('manage_equipment')) {
    return <div className="p-6 text-center text-gray-600">Sem permissão.</div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-wrap justify-between gap-4 items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="h-7 w-7 text-blue-600" />
            Cotações de Equipamentos
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Solicite propostas a fornecedores e, ao aceitar, o equipamento é cadastrado na fábrica.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNova(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700"
        >
          <Plus className="h-5 w-5" />
          Nova solicitação
        </button>
      </div>

      {msg && <p className="text-green-700 text-sm bg-green-50 p-3 rounded-lg">{msg}</p>}
      {err && <p className="text-red-700 text-sm bg-red-50 p-3 rounded-lg">{err}</p>}

      {catalogo.length > 0 && (
        <div className="bg-white border rounded-xl p-4">
          <h2 className="font-semibold flex items-center gap-2 text-gray-900 mb-3">
            <Store className="h-5 w-5" />
            Marketplace (catálogo publicado)
          </h2>
          <div className="grid md:grid-cols-2 gap-3 max-h-48 overflow-y-auto">
            {catalogo.slice(0, 12).map((c) => (
              <div key={c.id} className="border rounded-lg p-3 text-sm">
                <p className="font-medium">{c.titulo}</p>
                <p className="text-gray-500 text-xs">{c.tipo_nome} — {c.fornecedor_nome}</p>
                {c.preco_referencia > 0 && (
                  <p className="text-blue-700 mt-1">Ref. R$ {Number(c.preco_referencia).toLocaleString('pt-BR')}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="space-y-3">
          {solicitacoes.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Nenhuma solicitação ainda.</p>
          ) : (
            solicitacoes.map((s) => {
              const st = statusLabel(s.status);
              return (
                <div key={s.id} className="bg-white border rounded-xl p-4 flex flex-wrap justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-900">{s.tipo_equipamento_nome}</p>
                    <p className="text-sm text-gray-600">{s.descricao_necessidade || '—'}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {s.numero_cotacoes_recebidas || 0} cotação(ões) ·{' '}
                      {s.fornecedores_contatados?.length || 0} fornecedor(es)
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                    {s.status !== 'cancelada' && s.status !== 'equipamento_criado' && (
                      <>
                        <button
                          type="button"
                          onClick={() => abrirCotacoes(s)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Ver cotações
                        </button>
                        <button
                          type="button"
                          onClick={() => cancelar(s.id)}
                          className="text-sm text-red-600 hover:underline"
                        >
                          Cancelar
                        </button>
                      </>
                    )}
                    {s.equipamento_id && (
                      <span className="text-xs text-green-700 flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        Equip. {s.equipamento_id.slice(0, 8)}…
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {showNova && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-bold mb-4">Nova solicitação de cotação</h3>
            <form onSubmit={criarSolicitacao} className="space-y-3">
              <label className="block text-sm font-medium">
                Tipo de equipamento *
                <select
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  value={form.tipo_equipamento_id}
                  onChange={(e) => setForm((f) => ({ ...f, tipo_equipamento_id: e.target.value }))}
                  required
                >
                  <option value="">Selecione…</option>
                  {tipos.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome} ({t.nivel1_area})
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm font-medium">
                Apelido sugerido na fábrica
                <input
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  value={form.apelido_sugerido}
                  onChange={(e) => setForm((f) => ({ ...f, apelido_sugerido: e.target.value }))}
                />
              </label>
              <label className="block text-sm font-medium">
                Descrição da necessidade
                <textarea
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  rows={3}
                  value={form.descricao_necessidade}
                  onChange={(e) => setForm((f) => ({ ...f, descricao_necessidade: e.target.value }))}
                />
              </label>
              <label className="block text-sm font-medium">
                Prazo para resposta (dias)
                <input
                  type="number"
                  min={3}
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  value={form.dias_para_resposta}
                  onChange={(e) => setForm((f) => ({ ...f, dias_para_resposta: e.target.value }))}
                />
              </label>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setShowNova(false)} className="px-4 py-2 border rounded-lg">
                  Fechar
                </button>
                <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                  Enviar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {sel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-2">Cotações — {sel.tipo_equipamento_nome}</h3>
            {loadingCot ? (
              <Loader2 className="h-8 w-8 animate-spin mx-auto my-8" />
            ) : cotacoes.length === 0 ? (
              <p className="text-gray-500 py-6 flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Aguardando respostas dos fornecedores.
              </p>
            ) : (
              <ul className="space-y-3">
                {cotacoes.map((c) => (
                  <li key={c.id} className="border rounded-lg p-4">
                    <div className="flex justify-between flex-wrap gap-2">
                      <div>
                        <p className="font-medium">{c.fornecedor_nome}</p>
                        <p className="text-sm text-gray-600">
                          {c.fabricante} {c.modelo} — {c.capacidade_valor} {c.capacidade_unidade}
                        </p>
                        <p className="text-lg font-bold text-blue-700 mt-1">
                          R$ {Number(c.preco_total).toLocaleString('pt-BR')}
                        </p>
                        <p className="text-xs text-gray-500">Entrega em {c.prazo_entrega_dias} dias</p>
                      </div>
                      {c.aceita ? (
                        <span className="text-green-600 text-sm flex items-center gap-1">
                          <CheckCircle className="h-4 w-4" /> Aceita
                        </span>
                      ) : sel.status !== 'equipamento_criado' ? (
                        <button
                          type="button"
                          onClick={() => {
                            setAceiteCotacao(c);
                            setAceiteForm({
                              ...emptyAceite(),
                              apelido: sel.apelido_sugerido || sel.tipo_equipamento_nome,
                            });
                          }}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm"
                        >
                          Aceitar e cadastrar
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <button type="button" onClick={() => setSel(null)} className="mt-4 text-sm text-gray-600">
              Fechar
            </button>
          </div>
        </div>
      )}

      {aceiteCotacao && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="font-bold mb-3">Cadastrar equipamento na fábrica</h3>
            <p className="text-sm text-gray-600 mb-4">
              Fornecedor: {aceiteCotacao.fornecedor_nome} — R${' '}
              {Number(aceiteCotacao.preco_total).toLocaleString('pt-BR')}
            </p>
            <form onSubmit={confirmarAceite} className="space-y-3">
              <label className="block text-sm">
                Apelido *
                <input
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  value={aceiteForm.apelido}
                  onChange={(e) => setAceiteForm((f) => ({ ...f, apelido: e.target.value }))}
                  required
                />
              </label>
              <label className="block text-sm">
                Setor
                <input
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  value={aceiteForm.setor}
                  onChange={(e) => setAceiteForm((f) => ({ ...f, setor: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                Tag patrimônio
                <input
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  value={aceiteForm.tag_patrimonio}
                  onChange={(e) => setAceiteForm((f) => ({ ...f, tag_patrimonio: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                Vida útil (anos)
                <input
                  type="number"
                  min={1}
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  value={aceiteForm.vida_util_anos}
                  onChange={(e) => setAceiteForm((f) => ({ ...f, vida_util_anos: e.target.value }))}
                />
              </label>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setAceiteCotacao(null)} className="px-4 py-2 border rounded-lg">
                  Voltar
                </button>
                <button type="submit" disabled={loading} className="px-4 py-2 bg-green-600 text-white rounded-lg">
                  Confirmar compra
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CotacoesEquipamentosPage;
